// src/services/authService.js
import axios from 'axios';
import { message } from 'antd';

const PROXY_BASE_URL = 'https://specter-playground-proxy-server.azurewebsites.net';

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Create custom axios instance for authenticated requests ONLY
// This is ONLY for calls that go through the proxy server
const authAxios = axios.create({
  baseURL: PROXY_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Function to refresh auth token
const refreshAuthToken = async () => {
  try {
    const memberName = localStorage.getItem('memberName');
    const authToken = localStorage.getItem('authToken');
    const selectedOrgId = localStorage.getItem('selectedOrgId');
    const orgApiKeys = JSON.parse(localStorage.getItem('orgApiKeys') || '{}');
    const selectedEnv = localStorage.getItem('selectedEnv') || 'console';
    const devIp = localStorage.getItem('devIp') || '';
    const memberId = localStorage.getItem('memberId');
    
    if (!authToken) {
      throw new Error('No authentication token found');
    }

    // Get the appropriate API key based on environment
    let apiKey = '';
    if (selectedOrgId && orgApiKeys[selectedOrgId]) {
      const keys = orgApiKeys[selectedOrgId];
      if (selectedEnv === 'development') {
        apiKey = keys.devAPIKey || '';
      } else if (selectedEnv === 'staging') {
        apiKey = keys.stagingAPIKey || '';
      } else if (selectedEnv === 'demo' || selectedEnv === 'console') {
        apiKey = keys.productionAPIKey || '';
      }
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
      'Api-Key': apiKey,
      'env': selectedEnv,
      ...(selectedEnv === 'development' && devIp && { 'dev-ip': devIp.replace(/^https?:\/\//, '') })
    };

    // Use member ID if available
    const payload = memberId ? { memberId } : {};

    const response = await axios.post(
      `${PROXY_BASE_URL}/api/admin/v1/member/refresh-auth-token`,
      payload,
      { headers }
    );

    if (response.data?.status === 'success' && response.data?.data?.authToken) {
      const newAuthToken = response.data.data.authToken;
      
      // Update stored auth token
      localStorage.setItem('authToken', newAuthToken);
      
      // Update member details if returned
      if (response.data.data.memberDetails) {
        const memberDetails = response.data.data.memberDetails;
        if (memberDetails.name) {
          localStorage.setItem('memberName', memberDetails.name);
        }
        if (memberDetails.id) {
          localStorage.setItem('memberId', memberDetails.id);
        }
      }
      
      return newAuthToken;
    } else {
      throw new Error('Invalid refresh token response');
    }
  } catch (error) {
    console.error('Error refreshing auth token:', error);
    throw error;
  }
};

// Request interceptor - adds headers to all requests
authAxios.interceptors.request.use(
  (config) => {
    const authToken = localStorage.getItem('authToken');
    const selectedEnv = localStorage.getItem('selectedEnv') || 'console';
    const devIp = localStorage.getItem('devIp') || '';
    const selectedOrgId = localStorage.getItem('selectedOrgId');
    const orgApiKeys = JSON.parse(localStorage.getItem('orgApiKeys') || '{}');
    
    if (authToken) {
      config.headers['Authorization'] = `Bearer ${authToken}`;
    }
    
    // Set environment
    config.headers['env'] = selectedEnv;
    
    // Set dev IP if in development
    if (selectedEnv === 'development' && devIp) {
      config.headers['dev-ip'] = devIp.replace(/^https?:\/\//, '');
    }
    
    // Set API key based on environment
    if (selectedOrgId && orgApiKeys[selectedOrgId]) {
      const keys = orgApiKeys[selectedOrgId];
      let apiKey = '';
      
      if (selectedEnv === 'development') {
        apiKey = keys.devAPIKey || '';
      } else if (selectedEnv === 'staging') {
        apiKey = keys.stagingAPIKey || '';
      } else if (selectedEnv === 'demo' || selectedEnv === 'console') {
        apiKey = keys.productionAPIKey || '';
      }
      
      if (apiKey) {
        config.headers['Api-Key'] = apiKey;
      }
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handles 401 errors
authAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return authAxios(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      return new Promise((resolve, reject) => {
        refreshAuthToken()
          .then((newToken) => {
            authAxios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            processQueue(null, newToken);
            resolve(authAxios(originalRequest));
          })
          .catch((err) => {
            processQueue(err, null);
            
            // Clear auth data and redirect to login
            localStorage.removeItem('isLoggedIn');
            localStorage.removeItem('memberName');
            localStorage.removeItem('memberId');
            localStorage.removeItem('authToken');
            localStorage.removeItem('selectedOrgId');
            
            message.error('Your session has expired. Please log in again.');
            
            // Trigger re-render to show login modal
            window.dispatchEvent(new Event('storage'));
            
            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }
    
    return Promise.reject(error);
  }
);

export default authAxios;